/**
 * Stat-field catalogue for the configurable StatRow.
 *
 * A stat field is a metric a StatRow tile can bind to, drawn from the data the
 * front end can already reach:
 *   · `levelling:*`  → POST Report/levellingCounts        {total, levelled, inProgress, toBeLevelled}
 *   · `activity:*`   → POST workflow/userActivityAlerts/   {outstanding_tasks, overdue_tasks, due_tasks, unseen_notifications}
 *   · `docs:total`   → sum of the doc-type counts (docs store summary)
 *   · `doctype:<tmpl_name>` → that doc type's count        (docs store summary)
 *   · `status:<name>`        → that lifecycle status's count (docs store summary)
 *
 * `buildStatFields({docTypes, statuses})` produces the dropdown options (the
 * static metrics + the tenant's doc types/statuses). `resolveStat` reads a
 * value from the fetched/derived sources. `fieldMeta` gives default icon/colour/
 * label so a tile renders sensibly before the author customises it.
 */

const LEVELLING = [
  { value: 'levelling:total', label: 'Roles — total', source: 'levelling', key: 'total', icon: 'workspaces', colour: 'accent' },
  { value: 'levelling:levelled', label: 'Roles — levelled', source: 'levelling', key: 'levelled', icon: 'verified', colour: 'green' },
  { value: 'levelling:inProgress', label: 'Roles — in progress', source: 'levelling', key: 'inProgress', icon: 'pending', colour: 'accent' },
  { value: 'levelling:toBeLevelled', label: 'Roles — to be levelled', source: 'levelling', key: 'toBeLevelled', icon: 'schedule', colour: 'amber' },
];
const ACTIVITY = [
  { value: 'activity:outstanding_tasks', label: 'Tasks — outstanding', source: 'activity', key: 'outstanding_tasks', icon: 'assignment', colour: 'accent' },
  { value: 'activity:overdue_tasks', label: 'Tasks — overdue', source: 'activity', key: 'overdue_tasks', icon: 'warning', colour: 'rose' },
  { value: 'activity:due_tasks', label: 'Tasks — due soon', source: 'activity', key: 'due_tasks', icon: 'schedule', colour: 'amber' },
  { value: 'activity:unseen_notifications', label: 'Notifications — unread', source: 'activity', key: 'unseen_notifications', icon: 'notifications', colour: 'violet' },
];
const DOCS_TOTAL = { value: 'docs:total', label: 'Documents — total', source: 'docs', icon: 'description', colour: 'neutral' };

const STATIC_BY_VALUE = {};
[...LEVELLING, ...ACTIVITY, DOCS_TOTAL].forEach((f) => {
  STATIC_BY_VALUE[f.value] = f;
});

/** Grouped dropdown options. docTypes/statuses are the tenant's names. */
export function buildStatFields(options) {
  const { docTypes = [], statuses = [] } = options || {};
  const docFields = [
    DOCS_TOTAL,
    ...docTypes.map((t) => ({ value: 'doctype:' + t, label: 'Documents — ' + t, source: 'docs', icon: 'description', colour: 'neutral' })),
    ...statuses.map((s) => ({ value: 'status:' + s, label: 'Status — ' + s, source: 'docs', icon: 'flag', colour: 'neutral' })),
  ];
  return [
    { group: 'Levelling', fields: LEVELLING },
    { group: 'Tasks & activity', fields: ACTIVITY },
    { group: 'Documents', fields: docFields },
  ];
}

/** Flat option list (value + label), for a plain <select>. */
export function flatStatFields(options) {
  return buildStatFields(options).flatMap((g) => g.fields.map((f) => ({ value: f.value, label: f.label, group: g.group })));
}

/** The sources a set of tiles needs fetched (`levelling` / `activity` / `docs`). */
export function statSourcesFor(tiles) {
  const set = new Set();
  (tiles || []).forEach((t) => {
    const v = t && t.statField;
    if (typeof v !== 'string') return;
    if (v.startsWith('levelling:')) set.add('levelling');
    else if (v.startsWith('activity:')) set.add('activity');
    else if (v === 'docs:total' || v.startsWith('doctype:') || v.startsWith('status:')) set.add('docs');
  });
  return set;
}

/** Default icon/colour/label for a stat field (so tiles render before customisation). */
export function fieldMeta(value) {
  if (!value) return { icon: 'insights', colour: 'neutral', label: '' };
  if (STATIC_BY_VALUE[value]) return STATIC_BY_VALUE[value];
  if (value === 'docs:total') return DOCS_TOTAL;
  if (value.startsWith('doctype:')) return { icon: 'description', colour: 'neutral', label: 'Documents — ' + value.slice(8) };
  if (value.startsWith('status:')) return { icon: 'flag', colour: 'neutral', label: 'Status — ' + value.slice(7) };
  return { icon: 'insights', colour: 'neutral', label: value };
}

function findCount(list, name) {
  const row = (list || []).find((c) => String(c.name) === String(name));
  return row ? Number(row.count) || 0 : 0;
}

/** Does `name` pass an include/exclude list? Empty list = no constraint. */
function listMatches(name, list, mode) {
  if (!Array.isArray(list) || !list.length) return true;
  const inSet = list.some((d) => String(d) === String(name));
  return mode === 'exclude' ? !inSet : inSet;
}

/**
 * Resolve a stat field's numeric value from the available sources.
 *
 * `filter` (optional) scopes doc-count fields per tile — by doc type AND/OR by
 * status, each independently include/exclude. Whenever either constraint is
 * active the value is summed from the `typeStatusCounts` cross-tab, so a single
 * fetch serves tiles with any combination of scopes.
 *
 * @param {string} value
 * @param {{levelling?:Object, activity?:Object, docTypeCounts?:Array<{name,count}>, statusCounts?:Array<{name,count}>, typeStatusCounts?:Array<{type,status,count}>}} ctx
 * @param {{docTypes?:string[], docTypesMode?:'include'|'exclude', mode?:'include'|'exclude', statuses?:string[], statusesMode?:'include'|'exclude'}} [filter]
 * @returns {number|null}
 */
export function resolveStat(value, ctx, filter) {
  if (!value || typeof value !== 'string') return null;
  const c = ctx || {};
  const f = filter || {};
  const dts = Array.isArray(f.docTypes) ? f.docTypes.filter(Boolean) : [];
  const dtMode = f.docTypesMode === 'exclude' || f.mode === 'exclude' ? 'exclude' : 'include';
  const sts = Array.isArray(f.statuses) ? f.statuses.filter(Boolean) : [];
  const stMode = f.statusesMode === 'exclude' ? 'exclude' : 'include';
  const hasDT = dts.length > 0;
  const hasST = sts.length > 0;

  if (value.startsWith('levelling:')) {
    const k = value.slice('levelling:'.length);
    return c.levelling ? Number(c.levelling[k]) || 0 : null;
  }
  if (value.startsWith('activity:')) {
    const k = value.slice('activity:'.length);
    return c.activity ? Number(c.activity[k]) || 0 : null;
  }

  // Sum the (type × status) cross-tab under arbitrary type/status predicates.
  const sumXT = (typePred, statusPred) =>
    (c.typeStatusCounts || [])
      .filter((r) => typePred(r.type) && statusPred(r.status))
      .reduce((s, r) => s + (Number(r.count) || 0), 0);

  if (value === 'docs:total') {
    if (hasDT || hasST) {
      return sumXT((t) => listMatches(t, dts, dtMode), (s) => listMatches(s, sts, stMode));
    }
    return (c.docTypeCounts || []).reduce((s, x) => s + (Number(x.count) || 0), 0);
  }
  if (value.startsWith('doctype:')) {
    const type = value.slice('doctype:'.length);
    // Field is pinned to one type → only a STATUS scope narrows it further.
    if (hasST) return sumXT((t) => String(t) === String(type), (s) => listMatches(s, sts, stMode));
    return findCount(c.docTypeCounts, type);
  }
  if (value.startsWith('status:')) {
    const status = value.slice('status:'.length);
    // Field is pinned to one status → only a DOC-TYPE scope narrows it further.
    if (hasDT) return sumXT((t) => listMatches(t, dts, dtMode), (s) => String(s) === String(status));
    return findCount(c.statusCounts, status);
  }
  return null;
}
